const QUERY_PARAM_TO_CRITERIA_KEY = {
  specials: 'filter_specials',
  special: 'filter_specials',
  color: 'colors',
  colors: 'colors',
  color_combo: 'color_bubbles',
  color_bubbles: 'color_bubbles',
  maincategory: 'categories',
  maincategories: 'categories',
  categories: 'categories',
  category: 'products',
  products: 'products',
  variety: 'varieties',
  varieties: 'varieties',
  grade: 'grades',
  grades: 'grades',
  highlight: 'featured',
  featured: 'featured',
  country: 'origin',
  origin: 'origin',
  state: 'states',
  states: 'states',
  farm: 'vendors',
  vendors: 'vendors',
  vendor: 'vendors',
  unit: 'units',
  units: 'units',
  program: 'filter_program',
  coming_soon: 'coming_soon',
};

const FILTER_QUERY_PARAM_NAMES = new Set(Object.keys(QUERY_PARAM_TO_CRITERIA_KEY));

const DATE_QUERY_PARAM_NAMES = new Set(['deliverydate', 'delivery_date']);

const SEARCH_QUERY_PARAM_NAMES = new Set(['what', 'search', 's_search']);

const NON_FILTER_QUERY_KEYS = new Set([
  'tab',
  'per_page',
  'sort_by',
  'perPage',
  'sortBy',
  'page_number',
  'pageNumb',
  ...DATE_QUERY_PARAM_NAMES,
  ...SEARCH_QUERY_PARAM_NAMES,
  'from',
  'byMonth',
  'isEntire',
  'when',
  'entire_library',
  'entire_month',
  'criteria',
  'criteriaint',
  'f_criteria',
  'f_criteriaint',
]);

const CRITERIA_TO_FILTER_SECTION = {
  featured: { accordKey: 'featured', filterGroups: ['items'] },
  colors: { accordKey: 'colors', filterGroups: ['colors'] },
  color_bubbles: { accordKey: 'colors', filterGroups: ['color_bubbles'] },
  categories: { accordKey: 'categories', filterGroups: ['items'] },
  products: { accordKey: 'products', filterGroups: ['items'] },
  varieties: { accordKey: 'varieties', filterGroups: ['items'] },
  grades: { accordKey: 'grades', filterGroups: ['items'] },
  origin: { accordKey: 'origin', filterGroups: ['items'] },
  states: { accordKey: 'states', filterGroups: ['items'] },
  vendors: { accordKey: 'vendors', filterGroups: ['items'] },
  units: { accordKey: 'units', filterGroups: ['items', 'filter_unit'] },
  filter_specials: { accordKey: 'specials', filterGroups: ['filter_specials'] },
  filter_color: { accordKey: 'colors', filterGroups: ['colors'] },
  filter_color_combo: { accordKey: 'colors', filterGroups: ['color_bubbles'] },
  filter_maincategory: { accordKey: 'categories', filterGroups: ['items'] },
  filter_category: { accordKey: 'products', filterGroups: ['items'] },
  filter_variety: { accordKey: 'varieties', filterGroups: ['items'] },
  filter_grade: { accordKey: 'grades', filterGroups: ['items'] },
  filter_highlight: { accordKey: 'featured', filterGroups: ['items'] },
  filter_country: { accordKey: 'countries', filterGroups: ['filter_country', 'items'] },
  filter_state: { accordKey: 'states', filterGroups: ['items'] },
  filter_farm: { accordKey: 'vendors', filterGroups: ['items'] },
  filter_unit: { accordKey: 'units', filterGroups: ['items', 'filter_unit'] },
  filter_program: { accordKey: 'programs', filterGroups: ['filter_program'] },
};

const PROGRAM_FILTER_QUERY_RE = /^([a-z0-9]+)_(.+)$/i;

const isKnownUnprefixedShopParam = (key) => {
  const normalized = String(key || '').toLowerCase();
  return DATE_QUERY_PARAM_NAMES.has(normalized)
    || SEARCH_QUERY_PARAM_NAMES.has(normalized)
    || FILTER_QUERY_PARAM_NAMES.has(normalized);
};

// Keys like "delivery_date" / "color_bubbles" contain underscores but are global
// param names, not "{programcode}_{param}".
const isProgramPrefixedQueryKey = (key) => {
  const match = String(key || '').match(PROGRAM_FILTER_QUERY_RE);
  if (!match) return false;
  if (isKnownUnprefixedShopParam(key)) return false;
  return true;
};

const isNonFilterQueryKey = (key) => (
  NON_FILTER_QUERY_KEYS.has(key)
  || NON_FILTER_QUERY_KEYS.has(String(key || '').toLowerCase())
);

const normalizeFilterParamName = (name) => {
  const normalized = String(name || '').toLowerCase();
  if (FILTER_QUERY_PARAM_NAMES.has(normalized)) {
    return normalized;
  }
  return '';
};

export const normalizeQueryIds = (value) => {
  const values = Array.isArray(value) ? value : [value];
  return [...new Set(
    values
      .flatMap((entry) => String(entry ?? '').split(','))
      .map((id) => id.trim())
      .filter((id) => id !== '')
  )];
};

export const getCriteriaKeyForFilterParam = (filterParam) => (
  QUERY_PARAM_TO_CRITERIA_KEY[filterParam] || filterParam
);

export const getTabProgramCodeFromQuery = (query) => {
  const tab = query?.tab;
  if (!tab) return '';
  return String(Array.isArray(tab) ? tab[0] : tab).toLowerCase();
};

export const parseFilterEntriesFromQuery = (query, programCode = '') => {
  const code = String(programCode || '').toLowerCase();
  const entries = [];

  for (const [key, value] of Object.entries(query || {})) {
    if (isNonFilterQueryKey(key)) continue;

    let filterParam = '';

    if (isProgramPrefixedQueryKey(key)) {
      const [, queryProgram, filterName] = key.match(PROGRAM_FILTER_QUERY_RE);
      filterParam = normalizeFilterParamName(filterName);
      if (!filterParam) continue;
      if (code && queryProgram.toLowerCase() !== code) continue;
    } else {
      filterParam = normalizeFilterParamName(key);
      if (!filterParam) continue;
    }

    const ids = normalizeQueryIds(value);
    if (!ids.length) continue;

    entries.push({
      filterParam,
      criteriaKey: getCriteriaKeyForFilterParam(filterParam),
      ids,
    });
  }

  return entries;
};

const findFilterLabel = (filtersData, criteriaKey, id) => {
  const numericId = parseInt(id, 10);
  if (Number.isNaN(numericId)) return '';

  const path = CRITERIA_TO_FILTER_SECTION[criteriaKey];
  const sectionsToSearch = [];

  if (criteriaKey === 'origin' || criteriaKey === 'filter_country') {
    sectionsToSearch.push(
      { accordKey: 'origin', filterGroups: ['items'] },
      { accordKey: 'countries', filterGroups: ['filter_country', 'items'] },
    );
  } else if (path) {
    sectionsToSearch.push({ accordKey: path.accordKey, filterGroups: path.filterGroups });
  } else {
    Object.entries(filtersData || {}).forEach(([accordKey, section]) => {
      sectionsToSearch.push({
        accordKey,
        filterGroups: Object.keys(section || {}).filter((groupKey) => groupKey !== 'label'),
      });
    });
  }

  for (const { accordKey, filterGroups } of sectionsToSearch) {
    const section = filtersData?.[accordKey];
    if (!section) continue;

    for (const groupKey of filterGroups) {
      const group = section[groupKey];
      if (!Array.isArray(group)) continue;
      const match = group.find((node) => parseInt(node.id, 10) === numericId);
      if (match?.label) return match.label;
    }
  }

  return '';
};

const isSpecialsFilterParam = (filterParam) => (
  filterParam === 'specials' || filterParam === 'special'
);

export const mergeQueryFiltersIntoCriteria = (criteria, query, programCode = '', filtersData = null) => {
  const entries = parseFilterEntriesFromQuery(query, programCode);
  if (!entries.length) return false;

  for (const { criteriaKey, ids, filterParam } of entries) {
    if (!criteria[criteriaKey]) {
      criteria[criteriaKey] = {};
    }

    for (const id of ids) {
      const resolvedLabel = filtersData
        ? findFilterLabel(filtersData, criteriaKey, id)
        : '';
      const fallbackLabel = isSpecialsFilterParam(filterParam) ? 'Special' : String(id);
      criteria[criteriaKey][id] = resolvedLabel || criteria[criteriaKey][id] || fallbackLabel;
    }
  }

  return true;
};

export const hasQueryFilterParams = (query, programCode = '') => (
  parseFilterEntriesFromQuery(query, programCode).length > 0
);

const normalizeDateQueryParamName = (name) => (
  DATE_QUERY_PARAM_NAMES.has(String(name || '').toLowerCase()) ? 'deliveryDate' : ''
);

const normalizeSearchQueryParamName = (name) => (
  SEARCH_QUERY_PARAM_NAMES.has(String(name || '').toLowerCase()) ? 'search' : ''
);

const decodeSearchQueryValue = (value) => {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === undefined || raw === null || raw === '') return '';
  const text = String(raw);
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
};

const applySearchWidgetQueryValue = (target, kind, value) => {
  if (kind === 'deliveryDate') {
    const raw = Array.isArray(value) ? value[0] : value;
    if (raw === undefined || raw === null || raw === '') return;
    target.deliveryDate = String(raw);
    return;
  }

  if (kind === 'search') {
    const decoded = decodeSearchQueryValue(value);
    if (decoded) target.search = decoded;
  }
};

const applySearchWidgetParam = (target, paramName, value, programCode = '') => {
  let name = paramName;
  let applies = true;

  if (isProgramPrefixedQueryKey(paramName)) {
    const [, queryProgram, nestedName] = String(paramName).match(PROGRAM_FILTER_QUERY_RE);
    if (programCode && queryProgram.toLowerCase() !== programCode) {
      applies = false;
    }
    name = nestedName;
  }

  if (!applies) return;

  const dateKind = normalizeDateQueryParamName(name);
  if (dateKind) {
    applySearchWidgetQueryValue(target, 'deliveryDate', value);
    return;
  }

  const searchKind = normalizeSearchQueryParamName(name);
  if (searchKind) {
    applySearchWidgetQueryValue(target, 'search', value);
  }
};

export const parseSearchWidgetParamsFromQuery = (query, programCode = '') => {
  const code = String(programCode || '').toLowerCase();
  const params = { deliveryDate: '', search: '' };

  for (const [key, value] of Object.entries(query || {})) {
    if (isProgramPrefixedQueryKey(key)) continue;
    applySearchWidgetParam(params, key, value, code);
  }

  for (const [key, value] of Object.entries(query || {})) {
    if (!isProgramPrefixedQueryKey(key)) continue;
    applySearchWidgetParam(params, key, value, code);
  }

  return params;
};

export const hasQuerySearchWidgetParams = (query, programCode = '') => {
  const { deliveryDate, search } = parseSearchWidgetParamsFromQuery(query, programCode);
  return !!(deliveryDate || search);
};

const isUnprefixedGlobalShopParam = (key) => {
  if (isProgramPrefixedQueryKey(key)) return false;
  return isKnownUnprefixedShopParam(key);
};

export const hasGlobalUnprefixedShopParams = (query) => {
  for (const [key] of Object.entries(query || {})) {
    if (isUnprefixedGlobalShopParam(key)) return true;
  }
  return false;
};

/** Unprefixed `delivery_date` (e.g. homepage search with no program selected). */
export const getGlobalDeliveryDateFromQuery = (query) => {
  for (const [key, value] of Object.entries(query || {})) {
    if (isProgramPrefixedQueryKey(key) || !normalizeDateQueryParamName(key)) continue;
    const raw = Array.isArray(value) ? value[0] : value;
    if (raw !== undefined && raw !== null && raw !== '') {
      return normalizeShopDeliveryDate(raw);
    }
  }
  return '';
};

export const expandGlobalQueryParamsToProgramCodes = (query, programCodes = []) => {
  const codes = [...new Set(
    programCodes.map((code) => String(code || '').toLowerCase()).filter(Boolean)
  )];
  if (!codes.length) return { ...(query || {}) };

  const globalFilters = [];
  const globalSearchWidget = [];

  for (const [key, value] of Object.entries(query || {})) {
    if (isProgramPrefixedQueryKey(key)) continue;

    const dateKind = normalizeDateQueryParamName(key);
    if (dateKind) {
      globalSearchWidget.push({ param: 'delivery_date', value });
      continue;
    }

    const searchKind = normalizeSearchQueryParamName(key);
    if (searchKind) {
      globalSearchWidget.push({ param: 'what', value });
      continue;
    }

    if (isNonFilterQueryKey(key)) continue;

    const filterParam = normalizeFilterParamName(key);
    if (filterParam) {
      globalFilters.push({ key, value });
    }
  }

  if (!globalFilters.length && !globalSearchWidget.length) {
    return { ...(query || {}) };
  }

  const nextQuery = {};
  for (const [key, value] of Object.entries(query || {})) {
    // Keep non-filter keys, but drop unprefixed delivery_date / what (expanded below)
    if (!isNonFilterQueryKey(key)) continue;
    if (normalizeDateQueryParamName(key) || normalizeSearchQueryParamName(key)) continue;
    nextQuery[key] = value;
  }

  // Preserve already-prefixed program params that were not replaced by globals
  for (const [key, value] of Object.entries(query || {})) {
    if (!isProgramPrefixedQueryKey(key)) continue;
    const [, , nestedName] = key.match(PROGRAM_FILTER_QUERY_RE);
    const replacedByGlobalDate = normalizeDateQueryParamName(nestedName)
      && globalSearchWidget.some((entry) => entry.param === 'delivery_date');
    const replacedByGlobalSearch = normalizeSearchQueryParamName(nestedName)
      && globalSearchWidget.some((entry) => entry.param === 'what');
    const replacedByGlobalFilter = normalizeFilterParamName(nestedName)
      && globalFilters.some((entry) => entry.key === nestedName || normalizeFilterParamName(entry.key) === normalizeFilterParamName(nestedName));
    if (replacedByGlobalDate || replacedByGlobalSearch || replacedByGlobalFilter) continue;
    nextQuery[key] = value;
  }

  for (const code of codes) {
    for (const { key, value } of globalFilters) {
      nextQuery[`${code}_${key}`] = value;
    }

    for (const { param, value } of globalSearchWidget) {
      nextQuery[`${code}_${param}`] = value;
    }
  }

  return nextQuery;
};

export const normalizeShopDeliveryDate = (dateStr) => {
  if (!dateStr) return '';
  return String(dateStr).split(' ')[0];
};

export const resolveDeliveryDateAgainstAvailable = (requestedDate, availableDates = []) => {
  const requested = normalizeShopDeliveryDate(requestedDate);
  const dates = Array.isArray(availableDates) ? availableDates : [];
  const firstAvailable = normalizeShopDeliveryDate(dates[0]?.delivery_date || dates[0] || '');

  if (!requested) return firstAvailable;

  const match = dates.find((entry) => {
    const entryDate = normalizeShopDeliveryDate(
      typeof entry === 'string' ? entry : entry?.delivery_date
    );
    return entryDate && entryDate === requested;
  });

  if (match) return requested;
  return firstAvailable || requested;
};

export const getCartDeliveryDateForProgram = (cartMeta, programId, programCode = '') => {
  // Empty cart: do not use cart dates (keep URL / first-available behaviour)
  if (!(Number(cartMeta?.count) > 0) || !cartMeta.programs) return '';
  const programs = cartMeta.programs;
  const code = String(programCode || '').toLowerCase();
  const date = (programId != null
      ? (programs[programId] ?? programs[String(programId)] ?? programs[Number(programId)])
      : null)
    ?? (code ? programs[code] : null);
  return normalizeShopDeliveryDate(date);
};

export const isDeliveryDateAvailable = (date, availableDates = []) => {
  const requested = normalizeShopDeliveryDate(date);
  if (!requested) return false;

  return (Array.isArray(availableDates) ? availableDates : []).some((entry) => {
    const entryDate = normalizeShopDeliveryDate(
      typeof entry === 'string' ? entry : entry?.delivery_date ?? entry,
    );
    return entryDate === requested;
  });
};

export const removeAllProgramDeliveryDatesFromQuery = (query) => {
  const nextQuery = { ...(query || {}) };

  for (const key of Object.keys({ ...nextQuery })) {
    if (!isProgramPrefixedQueryKey(key)) continue;
    const match = String(key).match(PROGRAM_FILTER_QUERY_RE);
    if (!match) continue;
    if (!normalizeDateQueryParamName(match[2])) continue;
    delete nextQuery[key];
  }

  return nextQuery;
};

/** Product detail: keep only the active program's delivery date; drop invalid/unavailable dates. */
export const syncSelectedProgramDeliveryDateToQuery = (
  query,
  programCode,
  deliveryDate,
  availableDates = [],
) => {
  const nextQuery = removeAllProgramDeliveryDatesFromQuery(query);
  const code = String(programCode || '').toLowerCase();
  const normalizedDate = normalizeShopDeliveryDate(deliveryDate);

  if (code && normalizedDate && isDeliveryDateAvailable(normalizedDate, availableDates)) {
    nextQuery[`${code}_delivery_date`] = normalizedDate;
  }

  return nextQuery;
};

export const isAfterHoursShopProgram = (program) =>
  Number(program?.after_hour_status ?? program?.afterHoursStatus) === 1;

/** After-hours tabs use first_delivery_date in the UI only — no program delivery_date in the URL. */
export const removeAfterHoursProgramDeliveryDatesFromQuery = (query, programs = []) => {
  const nextQuery = { ...(query || {}) };
  let changed = false;

  (programs || []).forEach((program) => {
    if (!isAfterHoursShopProgram(program)) return;
    const code = String(program?.code || '').toLowerCase();
    if (!code) return;

    [`${code}_delivery_date`, `${code}_delivery_date`].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(nextQuery, key)) {
        delete nextQuery[key];
        changed = true;
      }
    });
  });

  return changed ? nextQuery : { ...(query || {}) };
};

export const applyCartProgramDeliveryDatesToQuery = (query, programs = [], cartMeta = null) => {
  if (!(Number(cartMeta?.count) > 0) || !cartMeta.programs) {
    return { ...(query || {}) };
  }

  const nextQuery = { ...(query || {}) };
  let changed = false;

  const writeCartDate = (code, cartDate) => {
    const normalizedCode = String(code || '').toLowerCase();
    const normalizedDate = normalizeShopDeliveryDate(cartDate);
    if (!normalizedCode || !normalizedDate) return;

    const key = `${normalizedCode}_delivery_date`;
    if (nextQuery[key] !== normalizedDate) {
      nextQuery[key] = normalizedDate;
      changed = true;
    }
    if (nextQuery[`${normalizedCode}_deliveryDate`] !== undefined) {
      delete nextQuery[`${normalizedCode}_deliveryDate`];
      changed = true;
    }
  };

  (programs || []).forEach((program) => {
    const code = String(program?.code || '').toLowerCase();
    if (!code) return;

    const cartDate = getCartDeliveryDateForProgram(cartMeta, program.id, code);
    if (cartDate) writeCartDate(code, cartDate);
  });

  // Include cart dates for programs not in the passed list (keyed by program code in cartMeta).
  Object.entries(cartMeta.programs).forEach(([key, date]) => {
    if (/^\d+$/.test(String(key))) return;
    writeCartDate(key, date);
  });

  return changed ? nextQuery : { ...(query || {}) };
};

const CRITERIA_KEY_TO_QUERY_PARAM = {
  filter_specials: 'specials',
  colors: 'colors',
  color_bubbles: 'color_bubbles',
  categories: 'categories',
  products: 'products',
  varieties: 'varieties',
  grades: 'grades',
  featured: 'featured',
  origin: 'origin',
  states: 'states',
  vendors: 'vendors',
  units: 'units',
  filter_program: 'program',
  filter_country: 'country',
  filter_state: 'state',
  filter_farm: 'farm',
  filter_unit: 'unit',
  filter_color: 'color',
  filter_color_combo: 'color_combo',
  filter_maincategory: 'maincategory',
  filter_category: 'products',
  filter_variety: 'variety',
  filter_grade: 'grade',
  filter_highlight: 'highlight',
  coming_soon: 'coming_soon',
  filter_coming_soon: 'coming_soon',
};

export const buildProgramFilterQueryFromCriteria = (criteria, programCode) => {
  const code = String(programCode || '').toLowerCase();
  const params = {};

  for (const [criteriaKey, idsObj] of Object.entries(criteria || {})) {
    const paramName = CRITERIA_KEY_TO_QUERY_PARAM[criteriaKey];
    if (!paramName) continue;

    const ids = Object.keys(idsObj || {})
      .map((id) => String(id).trim())
      .filter((id) => id !== '');
    if (!ids.length) continue;

    params[`${code}_${paramName}`] = ids.join(',');
  }

  return params;
};

export const mergeProgramCriteriaIntoQuery = (query, programCode, criteria) => {
  const code = String(programCode || '').toLowerCase();
  const nextQuery = { ...(query || {}) };

  for (const key of Object.keys(nextQuery)) {
    const match = String(key).match(PROGRAM_FILTER_QUERY_RE);
    if (!match || match[1].toLowerCase() !== code) continue;
    if (normalizeFilterParamName(match[2])) {
      delete nextQuery[key];
    }
  }

  return {
    ...nextQuery,
    ...buildProgramFilterQueryFromCriteria(criteria, programCode),
  };
};

export const mergeProgramSearchWidgetIntoQuery = (query, programCode, { deliveryDate, search } = {}) => {
  const code = String(programCode || '').toLowerCase();
  const nextQuery = { ...(query || {}) };

  if (deliveryDate !== undefined) {
    delete nextQuery[`${code}_deliveryDate`];
    delete nextQuery[`${code}_delivery_date`];
    if (deliveryDate) nextQuery[`${code}_delivery_date`] = deliveryDate;
  }

  if (search !== undefined) {
    delete nextQuery[`${code}_what`];
    if (search) nextQuery[`${code}_what`] = search;
  }

  return nextQuery;
};

export const readPerPageFromQuery = (query = {}) => {
  if ('per_page' in query) return parseInt(query.per_page, 10);
  if ('s_per_page' in query) return parseInt(query.s_per_page, 10);
  if ('perPage' in query) return parseInt(query.perPage, 10);
  if ('s_perPage' in query) return parseInt(query.s_perPage, 10);
  return null;
};

export const readSortByFromQuery = (query = {}) => {
  if ('sort_by' in query) return query.sort_by;
  if ('s_sort_by' in query) return query.s_sort_by;
  if ('sortBy' in query) return query.sortBy;
  if ('s_sortBy' in query) return query.s_sortBy;
  return null;
};

export const readPageNumberFromQuery = (query = {}) => {
  if ('page_number' in query) return parseInt(query.page_number, 10);
  if ('s_page_number' in query) return parseInt(query.s_page_number, 10);
  if ('pageNumb' in query) return parseInt(query.pageNumb, 10);
  if ('s_pageNumb' in query) return parseInt(query.s_pageNumb, 10);
  return null;
};

export const buildShopWithoutDateRouteQuery = (
  query,
  programCode,
  {
    criteria,
    deliveryDate,
    search,
    tab,
    perPage,
    sortBy,
    pageNumb,
  } = {},
) => {
  let nextQuery = { ...(query || {}) };
  const code = String(programCode || '').toLowerCase();

  if (tab) {
    nextQuery.tab = String(tab).toLowerCase();
  } else if (code) {
    nextQuery.tab = code;
  }

  if (criteria !== undefined) {
    nextQuery = mergeProgramCriteriaIntoQuery(nextQuery, programCode, criteria);
  }

  const widgetPatch = {};
  if (deliveryDate !== undefined) widgetPatch.deliveryDate = deliveryDate;
  if (search !== undefined) widgetPatch.search = search;
  if (Object.keys(widgetPatch).length) {
    nextQuery = mergeProgramSearchWidgetIntoQuery(nextQuery, programCode, widgetPatch);
  }

  if (perPage !== undefined) {
    delete nextQuery.perPage;
    delete nextQuery.s_perPage;
    delete nextQuery.s_per_page;
    nextQuery.per_page = String(perPage);
  }
  if (sortBy !== undefined) {
    delete nextQuery.sortBy;
    delete nextQuery.s_sortBy;
    delete nextQuery.s_sort_by;
    nextQuery.sort_by = sortBy;
  }
  if (pageNumb !== undefined) {
    delete nextQuery.pageNumb;
    delete nextQuery.s_pageNumb;
    delete nextQuery.s_page_number;
    if (pageNumb > 1) nextQuery.page_number = String(pageNumb);
    else delete nextQuery.page_number;
  }

  return nextQuery;
};

const CRITERIA_KEY_TO_API_ARRAY = {
  colors: 'colors',
  color_bubbles: 'color_bubbles',
  categories: 'categories',
  products: 'products',
  varieties: 'varieties',
  grades: 'grades',
  featured: 'featured',
  origin: 'origin',
  vendors: 'vendors',
  units: 'units',
};

export const buildRequestParametersFromQuery = (query, programCode = '', baseParameters = {}) => {
  const parameters = { ...baseParameters };
  const entries = parseFilterEntriesFromQuery(query, programCode);

  for (const { criteriaKey, ids } of entries) {
    if (criteriaKey === 'filter_specials') {
      parameters.is_special = 1;
      continue;
    }

    const apiKey = CRITERIA_KEY_TO_API_ARRAY[criteriaKey];
    if (!apiKey) continue;

    const numericIds = ids
      .map((id) => parseInt(id, 10))
      .filter((id) => !Number.isNaN(id));
    if (numericIds.length) {
      parameters[apiKey] = numericIds;
    }
  }

  const { deliveryDate, search } = parseSearchWidgetParamsFromQuery(query, programCode);
  if (deliveryDate) parameters.delivery_date = deliveryDate;
  if (search) parameters.search = search;

  const pageNumber = readPageNumberFromQuery(query);
  if (pageNumber > 0) parameters.page_number = pageNumber;

  const perPage = readPerPageFromQuery(query);
  if (perPage > 0) parameters.items_per_page = perPage;

  const sortBy = readSortByFromQuery(query);
  if (sortBy) parameters.sort_by = sortBy;

  return parameters;
};
